import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, X } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const Highlight = ({ text, q }) => {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (!q || i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark style={{ background: "#FEF3C7", color: "inherit", padding: 0 }}>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
};

export const campaignSummaryLine = (c) => {
  const created = c.created_at ? new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  return [
    cap(c.status), cap(c.type),
    (c.product_tags || []).map(t => String(t).toUpperCase()).join("/") || null,
    `${(c.audience || 0).toLocaleString()} audience`,
    `${(c.contacted || 0).toLocaleString()} contacted`,
    `${(c.connected || 0).toLocaleString()} connected`,
    `${c.meetings || 0} meetings`,
    `${c.connect_rate || 0}% connect`,
    created, c.created_by_name || null,
  ].filter(Boolean).join(" · ");
};

// Type-to-search campaign picker for the Analytics filter bar.
export function CampaignSearchFilter({ value, selectedName, onChange, token }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  const browsing = q.trim() === "";
  const loadMore = async () => {
    if (!browsing || busy || items.length >= total) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API_BASE}/dialer/analytics/campaign-search`, {
        params: { browse: true, offset: items.length, limit: 50 }, headers: { Authorization: `Bearer ${token}` },
      });
      setItems(prev => [...prev, ...(data.items || []).filter(c => !prev.some(p => p.id === c.id))]);
      setTotal(data.total || 0);
    } catch (_e) { /* keep what is shown */ } finally { setBusy(false); }
  };

  useEffect(() => {
    const t = q.trim();
    setItems([]); setTotal(0);
    if (t.length > 0 && t.length < 3) return undefined;
    if (!t && !open) return undefined;
    let cancel = false;
    setBusy(true);
    const h = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/dialer/analytics/campaign-search`, {
          params: t ? { q: t, limit: 20 } : { browse: true, offset: 0, limit: 50 }, headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancel) { setItems(data.items || []); setTotal(data.total || 0); setActive(0); }
      } catch (_e) {
        if (!cancel) { setItems([]); setTotal(0); }
      } finally {
        if (!cancel) setBusy(false);
      }
    }, t ? 250 : 0);
    return () => { cancel = true; clearTimeout(h); };
  }, [q, token, open]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (c) => { onChange(c.id, c.name); setQ(""); setOpen(false); };
  const onKey = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (!open) {
      if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); }
      return;
    }
    if (!items.length || busy) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, items.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); pick(items[active]); }
  };
  const shell = { display: "flex", alignItems: "center", gap: 6, border: "0.5px solid #ddd", borderRadius: 6, padding: "3px 8px", fontSize: 12, background: "#fff", width: 280 };

  if (value) {
    return (
      <div data-testid="analytics-campaign-selected" style={shell}>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={selectedName}>{selectedName || value}</span>
        <button data-testid="analytics-campaign-clear" onClick={() => onChange("", "")} aria-label="Clear campaign" style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex", color: "#888" }}>
          <X style={{ width: 12, height: 12 }} />
        </button>
      </div>
    );
  }
  const t = q.trim();
  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <div style={shell}>
        <Search style={{ width: 12, height: 12, color: "#888" }} />
        <input data-testid="analytics-campaign-search" value={q} placeholder="All campaigns"
          onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
          style={{ border: "none", outline: "none", fontSize: 12, flex: 1, fontFamily: "var(--font)", background: "transparent" }} />
      </div>
      {open && (
        <div data-testid="analytics-campaign-results" onScroll={e => { const el = e.currentTarget; if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) loadMore(); }} style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, width: 460, maxHeight: 340, overflowY: "auto", background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 8, boxShadow: "0 8px 24px rgba(15,23,42,0.12)", zIndex: 30 }}>
          {t.length > 0 && t.length < 3 && <div data-testid="analytics-campaign-hint" style={{ padding: "10px 12px", fontSize: 11, color: "#888" }}>Type at least 3 letters</div>}
          {(t.length >= 3 || browsing) && busy && !items.length && <div style={{ padding: "10px 12px", fontSize: 11, color: "#888" }}>Searching…</div>}
          {t.length >= 3 && !busy && !items.length && <div data-testid="analytics-campaign-nomatch" style={{ padding: "10px 12px", fontSize: 11, color: "#888" }}>No campaigns match</div>}
          {items.map((c, i) => (
            <div key={c.id} data-testid={`analytics-campaign-option-${c.id}`} onMouseDown={e => { e.preventDefault(); pick(c); }} onMouseEnter={() => setActive(i)}
              style={{ padding: "8px 12px", cursor: "pointer", background: i === active ? "#F1F5F9" : "#fff", borderBottom: "0.5px solid #f3f4f6" }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "#111" }}><Highlight text={c.name || "(untitled)"} q={t} /></div>
              <div style={{ fontSize: 10.5, color: "#888", marginTop: 2 }}>{campaignSummaryLine(c)}</div>
            </div>
          ))}
          {browsing && items.length > 0 && <div data-testid="analytics-campaign-footer" style={{ padding: "6px 12px", fontSize: 10.5, color: "#888" }}>{busy ? "Loading more…" : items.length < total ? `Showing ${items.length} of ${total} — scroll for more` : `All ${total} campaigns`}</div>}
          {!browsing && total > items.length && <div data-testid="analytics-campaign-footer" style={{ padding: "6px 12px", fontSize: 10.5, color: "#888" }}>Showing {items.length} of {total} — keep typing to narrow</div>}
        </div>
      )}
    </div>
  );
}
